import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    Users,
    Send,
    Mail,
    Percent,
    Coins,
    Bot,
    Rocket,
    Upload,
    LineChart,
    ArrowUpRight,
    Sparkles,
} from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { TEST_IDS } from "@/constants/testIds";
import VisitorsPanel from "@/components/dashboard/VisitorsPanel";
import AdSlot from "@/components/AdSlot";
import OnboardingWizard from "@/components/OnboardingWizard";

export default function Dashboard() {
    const { user } = useAuth();
    const { t } = useI18n();
    const [stats, setStats] = useState(null);
    const [showOnboarding, setShowOnboarding] = useState(false);

    useEffect(() => {
        api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
        api.get("/onboarding/status")
            .then((r) => setShowOnboarding(!r.data.completed))
            .catch(() => {});
    }, []);

    const widgets = [
        { key: "leads", label: t.app.widgetLeads, value: stats?.leads_total ?? 0, icon: Users, tone: "indigo" },
        { key: "campaigns", label: t.app.widgetCampaigns, value: stats?.campaigns_active ?? 0, icon: Send, tone: "violet" },
        { key: "emails", label: t.app.widgetEmails, value: stats?.emails_sent ?? 0, icon: Mail, tone: "cyan" },
        { key: "reply", label: t.app.widgetReply, value: `${((stats?.reply_rate ?? 0) * 100).toFixed(1)}%`, icon: Percent, tone: "cyan" },
        { key: "credits", label: t.app.widgetCredits, value: user?.credits ?? 0, icon: Coins, tone: "indigo" },
    ];

    return (
        <div className="px-6 lg:px-10 py-8">
            {showOnboarding && (
                <OnboardingWizard
                    onComplete={() => setShowOnboarding(false)}
                    onSkip={() => setShowOnboarding(false)}
                />
            )}

            {/* Header */}
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan">
                        {t.app.welcomeBack}, {user?.name?.split(" ")[0]}
                    </div>
                    <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl tracking-tighter">
                        {t.app.dashboardTitle}
                    </h1>
                    <p className="mt-1.5 text-zinc-500 text-sm">{t.app.dashboardSub}</p>
                </div>

                <div className="flex flex-wrap gap-2">
                    <QuickAction to="/app/find" icon={Rocket} label={t.app.actionProspect} accent />
                    <QuickAction to="/app/find" icon={Upload} label={t.app.actionImport} />
                    <QuickAction to="/app/engage" icon={Send} label={t.app.actionCampaign} />
                    <QuickAction to="/app/copilot" icon={Bot} label={t.app.actionCopilot} />
                </div>
            </div>

            {/* Widgets row */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {widgets.map((w, i) => (
                    <motion.div
                        key={w.key}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: i * 0.05 }}
                        data-testid={TEST_IDS.dashboard.widget(w.key)}
                        className="relative bg-brand-surface border border-white/5 rounded-2xl p-5 overflow-hidden group"
                    >
                        <div className="flex items-start justify-between">
                            <div className="text-xs text-zinc-500">{w.label}</div>
                            <w.icon size={16} className={`text-brand-${w.tone}`} />
                        </div>
                        <div className="mt-4 font-display font-black text-3xl tracking-tighter">{w.value}</div>
                        <div className="mt-1 text-xs text-zinc-600 font-mono">Phase 1 · skeleton</div>
                    </motion.div>
                ))}
            </div>

            {/* Main grid */}
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* AI Insight — big */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.15 }}
                    data-testid={TEST_IDS.dashboard.aiInsight}
                    className="lg:col-span-2 relative rounded-2xl p-8 overflow-hidden"
                    style={{
                        background: "radial-gradient(ellipse at 20% 0%, rgba(139,92,246,0.35), transparent 60%), radial-gradient(ellipse at 100% 100%, rgba(6,182,212,0.25), transparent 55%), #14142a",
                    }}
                >
                    <div className="absolute inset-0 bg-grain opacity-40 pointer-events-none" />
                    <div className="relative flex items-start gap-4">
                        <div className="w-11 h-11 shrink-0 rounded-xl bg-gradient-to-br from-brand-indigo to-brand-violet flex items-center justify-center shadow-[0_0_30px_-6px_rgba(139,92,246,0.7)]">
                            <Sparkles size={18} className="text-white" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan">{t.app.aiInsightTitle}</div>
                            <p className="mt-3 text-lg text-white leading-relaxed">{t.app.aiInsightBody}</p>
                            <div className="mt-6 flex flex-wrap gap-2">
                                <Link
                                    to="/app/find"
                                    className="btn-cta text-white font-semibold px-4 py-2 rounded-full text-sm inline-flex items-center gap-1.5"
                                >
                                    {t.app.actionProspect}
                                    <ArrowUpRight size={14} />
                                </Link>
                                <Link
                                    to="/app/find"
                                    className="text-white font-medium px-4 py-2 rounded-full text-sm border border-white/12 hover:border-white/25 hover:bg-white/5 transition"
                                >
                                    {t.app.actionImport}
                                </Link>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Weekly Activity */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="bg-brand-surface border border-white/5 rounded-2xl p-6"
                >
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-white">{t.app.weeklyActivity}</div>
                        <LineChart size={14} className="text-zinc-500" />
                    </div>
                    <div className="mt-4 h-52 min-h-[200px] w-full">
                        <ResponsiveContainer width="100%" height="100%" minHeight={180}>
                            <AreaChart data={stats?.weekly_trend ?? []} margin={{ top: 10, right: 4, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="lg" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#6366F1" stopOpacity={0.5} />
                                        <stop offset="100%" stopColor="#6366F1" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis dataKey="day" tick={{ fill: "#71717A", fontSize: 11 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: "#71717A", fontSize: 11 }} axisLine={false} tickLine={false} />
                                <Tooltip
                                    contentStyle={{
                                        background: "rgba(26,26,46,0.9)",
                                        border: "1px solid rgba(255,255,255,0.1)",
                                        borderRadius: 8,
                                        fontSize: 12,
                                        color: "#fff",
                                    }}
                                />
                                <Area type="monotone" dataKey="value" stroke="#6366F1" fill="url(#lg)" strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="text-xs text-zinc-600 mt-2 font-mono">No activity yet — start prospecting to see the line move.</div>
                </motion.div>
            </div>

            {/* My personal zone — live visitor analytics */}
            <VisitorsPanel />

            {/* One discreet AdSense slot — never on Billing/Settings/Copilot */}
            <AdSlot slot="4444444444" format="auto" testId="dashboard-ad" className="mt-6 opacity-70 hover:opacity-100 transition" />
        </div>
    );
}

function QuickAction({ to, icon: Icon, label, accent }) {
    return (
        <Link
            to={to}
            className={
                accent
                    ? "btn-cta text-white font-semibold text-sm px-4 py-2 rounded-full inline-flex items-center gap-2"
                    : "text-white font-medium text-sm px-4 py-2 rounded-full border border-white/12 hover:border-white/25 hover:bg-white/5 transition inline-flex items-center gap-2"
            }
        >
            <Icon size={14} />
            {label}
        </Link>
    );
}
